

import { Button, Alert, DiscardChangesDialog, FormField, TextInput, SuccessNotice } from "@a2z/ui";

import { useRef, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { useForm } from "react-hook-form";
import { keys } from "@/lib/i18n/portal";
import { useFormSafety } from "@/lib/navigation/useFormSafety";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useUpdateProfilePasswordMutation } from "@/modules/profile/services/useUpdateProfilePasswordMutation";
import { FormActionButtons } from "./FormActionButtons";

export function ProfilePasswordForm() {
  const t = useTranslations();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNextPassword, setShowNextPassword] = useState(false);
  const { mutateAsync: updateProfilePassword } =
    useUpdateProfilePasswordMutation();
  const formRef = useRef<HTMLFormElement | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<{ currentPassword: string; nextPassword: string }>({
    defaultValues: {
      currentPassword: "",
      nextPassword: "",
    },
  });
  const { promptDiscard, discardDialogState } = useFormSafety({
    isDirty,
    onDiscard: () => {
      reset();
      setError(null);
      setSuccess(false);
    },
  });

  const resetForm = () => {
    reset();
    setError(null);
    setSuccess(false);
    setShowCurrentPassword(false);
    setShowNextPassword(false);
  };

  const handleCancel = () => promptDiscard(resetForm);

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    setSuccess(false);

    try {
      await updateProfilePassword(values);
    } catch (error) {
      if (error instanceof ApiJsonClientError) {
        setError(error.message || t(keys.errors.generic));
      } else {
        setError(t(keys.errors.generic));
      }
      return;
    }

    resetForm();
    setSuccess(true);
  });

  return (
    <>
      <form
        ref={formRef}
        onSubmit={onSubmit}
        className="mx-auto flex w-full max-w-[640px] flex-col gap-4"
      >
        {error ? <Alert message={error} helperText={t(keys.errors.recovery)} /> : null}
        {success ? (
          <SuccessNotice title={t(keys.common.updateSuccess)} />
        ) : null}
        <div className="flex flex-col gap-4 px-2 sm:px-3">
          <FormField
            label={t(keys.admin.currentPasswordLabel)}
            name="currentPassword"
            required
            error={errors.currentPassword?.message}
          >
            <div className="relative">
              <TextInput
                type={showCurrentPassword ? "text" : "password"}
                placeholder={t(keys.placeholders.password)}
                required
                hasError={Boolean(errors.currentPassword)}
                {...register("currentPassword", {
                  required: t(keys.errors.required),
                })}
              />
              <Button variant="ghost"
                type="button"
                onClick={() => setShowCurrentPassword((value) => !value)}
                className="absolute inset-y-0 end-2 my-auto rounded-md px-2 text-xs font-semibold text-[color:var(--text-muted)]"
              >
                {showCurrentPassword ? t(keys.common.hide) : t(keys.common.show)}
              </Button>
            </div>
          </FormField>
          <FormField
            label={t(keys.admin.newPasswordLabel)}
            name="nextPassword"
            required
            helperText={t(keys.auth.passwordHint)}
            error={errors.nextPassword?.message}
          >
            <div className="relative">
              <TextInput
                type={showNextPassword ? "text" : "password"}
                placeholder={t(keys.placeholders.password)}
                required
                hasError={Boolean(errors.nextPassword)}
                {...register("nextPassword", {
                  required: t(keys.errors.required),
                  minLength: { value: 8, message: t(keys.auth.passwordHint) },
                })}
              />
              <Button variant="ghost"
                type="button"
                onClick={() => setShowNextPassword((value) => !value)}
                className="absolute inset-y-0 end-2 my-auto rounded-md px-2 text-xs font-semibold text-[color:var(--text-muted)]"
              >
                {showNextPassword ? t(keys.common.hide) : t(keys.common.show)}
              </Button>
            </div>
          </FormField>
          <div
            className={`mt-4 overflow-hidden transition-all duration-300 ${
              isDirty || isSubmitting
                ? "max-h-24 translate-y-0 opacity-100"
                : "pointer-events-none max-h-0 -translate-y-1 opacity-0"
            }`}
            aria-hidden={!isDirty && !isSubmitting}
          >
            <div className={isDirty || isSubmitting ? "pb-1" : "pb-0"}>
              <FormActionButtons
                cancelLabel={t(keys.common.cancel)}
                saveLabel={t(keys.common.update)}
                loadingLabel={t(keys.common.loading)}
                onCancel={handleCancel}
                isSaving={isSubmitting}
              />
            </div>
          </div>
        </div>
      </form>
      <DiscardChangesDialog
        {...discardDialogState}
        title={t(keys.forms.discardChangesTitle)}
        body={t(keys.forms.discardChangesBody)}
        discardLabel={t(keys.forms.discardChangesDiscard)}
        continueLabel={t(keys.forms.discardChangesContinue)}
      />
    </>
  );
}
